import React from 'react';

import { Alert, Button, Divider, Group, LoadingOverlay, Radio, Stack } from '@mantine/core';
import { IconExclamationCircle } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';

import useOwnAthleteProfileAndSubAccountProfiles from './useOwnAthleteProfileAndSubAccountProfiles';
import { calculateEmail, calculateMyselfLabel } from './utils';
import {
  INITIAL_AGREEMENT_WAIVER_VALUES,
  INITIAL_ATHLETE_INFORMATION_VALUES,
  useCheckout,
  WhoIsThisFormOption,
} from '../../CheckoutContext';

const WhoIsThisFor = (): JSX.Element => {
  const { session } = useAuth();
  const { form, packageData, completeStep, setAthleteInfoWasPrepopulated } = useCheckout();

  const handleContinue = (): void => {
    completeStep('whoIsThisFor');
  };

  const gymId = packageData.gym.gymId;

  const {
    data: profilesData,
    isLoading: profilesLoading,
    isError: profilesError,
  } = useOwnAthleteProfileAndSubAccountProfiles(gymId);

  if (profilesLoading) {
    return <LoadingOverlay visible={true} />;
  }

  if (profilesError) {
    return (
      <Alert icon={<IconExclamationCircle size='md' />} title='Error' color='red'>
        Something went wrong while fetching data!
      </Alert>
    );
  }

  const { ownAthleteProfile, subAccountProfiles } = profilesData ?? {};

  const myselfLabel = calculateMyselfLabel(session, ownAthleteProfile);

  const getRadioValue = (): string => {
    const { whoIsThisFor, profileId } = form.values;
    if (whoIsThisFor === 'subAccount' && profileId) {
      return `subAccount:${profileId}`;
    }

    return whoIsThisFor;
  };

  const handleWhoIsThisForChange = (value: string): void => {
    const [whoIsThisFor, profileId] = value.split(':') as [WhoIsThisFormOption, string | undefined];

    form.setFieldValue('whoIsThisFor', whoIsThisFor);
    form.setFieldValue('profileId', profileId ?? undefined);

    // reset athlete information — and the agreement step, whose waiver requirement
    // and signature belong to the previously selected purchase target
    const email = calculateEmail(whoIsThisFor, session, profileId, subAccountProfiles ?? []);
    form.setValues({
      ...form.values,
      whoIsThisFor,
      profileId: profileId ?? undefined,
      athleteInformation: { ...INITIAL_ATHLETE_INFORMATION_VALUES, email },
      agreementWaiver: { ...INITIAL_AGREEMENT_WAIVER_VALUES },
    });

    setAthleteInfoWasPrepopulated(false);
  };

  return (
    <Stack>
      <Stack spacing='sm' px='xl'>
        <Radio.Group value={getRadioValue()} onChange={handleWhoIsThisForChange}>
          <Stack spacing='sm' my='lg'>
            <Radio value={`myself`} label={myselfLabel} />
            {subAccountProfiles?.map(profile => (
              <Radio
                key={profile.profileId}
                value={`subAccount:${profile.profileId}`}
                label={`${profile.fullName} ${profile.isDependent ? '(Dependent)' : '(Linked)'}`}
              />
            ))}
            <Divider />
            <Radio
              value={`newLinkedAccount`}
              label='Add a Linked Profile (unique email/login)'
              disabled={ownAthleteProfile?.isSubAccount}
            />
            <Radio
              value={`newDependentAccount`}
              label='Add a Dependent Profile (managed by you)'
              disabled={ownAthleteProfile?.isSubAccount}
            />
          </Stack>
        </Radio.Group>
      </Stack>
      <Group position='right'>
        <Button data-cy='whoIsThisForContinue' onClick={handleContinue}>
          Continue
        </Button>
      </Group>
    </Stack>
  );
};

export default WhoIsThisFor;
